import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Portal ancorado a um elemento (popover) ou fixo à direita da viewport
// (painel lateral). Nunca `absolute` dentro de um container com overflow —
// é a causa do bug SUS-01 do modal de convite, citada em
// .claude/rbac/arquitetura-v2.md §6.11. Fecha em clique fora e em Esc.
export default function AnchoredPortal({
  anchorRef,
  onClose,
  placement = 'below',
  width = 380,
  maxHeight = '60vh',
  className = '',
  children,
}) {
  const [pos, setPos] = useState(placement === 'right-panel' ? {} : null);
  const boxRef = useRef(null);

  useLayoutEffect(() => {
    if (placement === 'right-panel') return;
    const recompute = () => {
      const rect = anchorRef?.current?.getBoundingClientRect();
      if (!rect) return;
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const boxHeight = boxRef.current?.offsetHeight || 320;
      const spaceBelow = vh - rect.bottom;
      const flipUp = spaceBelow < boxHeight + 8 && rect.top > spaceBelow;
      const top = flipUp
        ? rect.top + window.scrollY - boxHeight - 4
        : rect.bottom + window.scrollY + 4;
      let left = rect.left + window.scrollX;
      if (left + width > vw - 8) left = Math.max(8, vw - width - 8);
      setPos({ top, left });
    };
    recompute();
    const raf = requestAnimationFrame(recompute);
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
    };
  }, [anchorRef, placement, width]);

  useEffect(() => {
    const handleClick = (e) => {
      if (boxRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose();
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [anchorRef, onClose]);

  if (placement !== 'right-panel' && !pos) return null;

  const style = placement === 'right-panel'
    ? { position: 'fixed', top: 0, right: 0, bottom: 0, width, zIndex: 9999 }
    : { position: 'absolute', top: pos.top, left: pos.left, width, maxHeight, zIndex: 9999 };

  const baseClass = placement === 'right-panel'
    ? 'bg-card border-l border-border shadow-2xl flex flex-col'
    : 'bg-card border border-border rounded-lg shadow-2xl flex flex-col overflow-hidden';

  return createPortal(
    <div ref={boxRef} style={style} className={`${baseClass} ${className}`}>
      {children}
    </div>,
    document.body
  );
}
